import { useMemo } from "react";

import { Box, Chip, Stack, Typography } from "@mui/material";

import { FilterModeOption } from "../../Pages/CloudAnalytics/allocations/types";
import { type AttributionFilter, type MetadataOption } from "../../types";
import { DimensionSelector } from "../Selects/CloudAnalytics/DimensionSelector";
import AllocationRuleChips from "./AllocationRuleChips";

const PROPERTY_FIELD_WIDTH = 250;

type Props = {
  selectedDimension: MetadataOption | null;
  dimensions: MetadataOption[];
  disabled: boolean;
  filter?: AttributionFilter;
  excludeSelectMetadataIds?: Set<string>;
  label?: string;
  placeholder?: string;
  onEdit: () => void;
  onDimensionChanged: (option: MetadataOption | null) => void;
};

export default function DimensionSelectionRow({
  label,
  filter,
  selectedDimension,
  dimensions,
  disabled = false,
  placeholder,
  excludeSelectMetadataIds,
  onDimensionChanged,
  onEdit,
}: Props) {
  const mode = useMemo(() => {
    if (filter?.regexp) {
      return filter.inverse ? FilterModeOption.DOES_NOT_MATCHES_REGEX : FilterModeOption.MATCHES_REGEX;
    }

    switch (filter?.mode) {
      case "is":
        return filter?.inverse ? FilterModeOption.IS_NOT : FilterModeOption.IS;
      case "contains":
        return filter?.inverse ? FilterModeOption.DOES_NOT_CONTAIN : FilterModeOption.CONTAINS;
      case "starts_with":
        return FilterModeOption.STARTS_WITH;
      case "ends_with":
        return FilterModeOption.ENDS_WITH;
      case "regexp":
        return filter?.inverse ? FilterModeOption.DOES_NOT_MATCHES_REGEX : FilterModeOption.MATCHES_REGEX;
      default:
        return filter?.inverse ? FilterModeOption.IS_NOT : FilterModeOption.IS;
    }
  }, [filter]);

  const isRegex = Boolean(filter?.regexp);

  return (
    <Stack spacing={1.25} direction={{ xs: "column", md: "row" }} alignContent="center" sx={{ mb: 1 }}>
      <Box sx={{ width: { md: PROPERTY_FIELD_WIDTH } }}>
        <DimensionSelector
          onDimensionSelected={onDimensionChanged}
          selectedDimension={selectedDimension}
          dimensions={dimensions}
          placeholder="select"
          disabled={disabled}
          onEdit={onEdit}
          excludeSelectMetadataIds={excludeSelectMetadataIds}
          textFieldProps={{
            variant: "outlined",
            margin: "dense",
            size: "small",
            sx: ({ palette }) => ({
              margin: 0,
              backgroundColor: palette.general.backgroundDefault,
            }),
            label,
            placeholder,
          }}
        />
      </Box>
      {filter && (
        <Stack direction="row" sx={{ alignItems: "center", gap: 1.25 }}>
          <Typography variant="button" sx={{ fontWeight: 500, textTransform: "lowercase" }}>
            {mode}
          </Typography>

          <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-start" flexWrap="wrap">
            {isRegex ? (
              <Chip
                label={filter.regexp}
                size="small"
                color="primary"
                sx={{ maxWidth: filter.inverse ? 290 : 340 }}
                variant="outlined"
              />
            ) : (
              <AllocationRuleChips
                filter={filter}
                ruleFilterWidth={mode.length}
                nullFallback={null}
                title={selectedDimension?.data.label ?? ""}
              />
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
